import { useCallback, useState } from 'react'
import { returnOrderBy } from '~/pages/backoffice/sorter'

/** Taille de page par défaut des tableaux du back-office. */
export const DEFAULT_PAGE_SIZE = 10

/**
 * Tailles proposées par le sélecteur du pied de tableau.
 *
 * Plafonnées à 100 alors que l'API en accepte 1000 (`MAX_PAGE_SIZE`) : au-delà
 * de cent lignes, on ne lit plus un tableau, on cherche dedans — et c'est aux
 * filtres et à l'export CSV de servir ce besoin, pas à la pagination.
 */
export const PAGE_SIZE_OPTIONS = [10, 25, 50, 100] as const

/**
 * Page à afficher après un changement de taille, choisie pour que la PREMIÈRE
 * LIGNE de la page courante reste visible.
 *
 * Un simple retour à la page 1 aurait été plus court, mais il fait perdre sa
 * place à quelqu'un qui parcourt un tableau long : passer à 50 lignes pour
 * « voir plus » depuis la page 6 le renvoyait au tout début, c'est-à-dire à
 * l'opposé de ce qu'il demandait.
 *
 * Fonction pure et exportée pour être épinglée sans monter de composant : c'est
 * de l'arithmétique d'indices, le genre qui se trompe d'un rang en silence.
 */
export function pageForNewSize(page: number, currentSize: number, nextSize: number): number {
  const firstRowIndex = (page - 1) * currentSize
  return Math.floor(firstRowIndex / nextSize) + 1
}

/**
 * Dernière page qui existe pour `total` lignes, 1 au minimum. Une page
 * paginée côté serveur peut se vider sous les pieds (dernière ligne supprimée,
 * ou sortie du filtre) : l'écran y revient plutôt que d'afficher une page vide.
 */
export function lastPage(total: number, pageSize: number): number {
  return Math.max(1, Math.ceil(total / pageSize))
}

export type SortOrder = 'ascend' | 'descend'

export type SortedInfo = {
  field: string
  order: SortOrder
}

/**
 * État de pagination et de tri partagé par les tableaux paginés du back-office
 * (utilisateurs, statistiques), avec le bloc `skip`/`take`/`orderBy` déjà
 * calculé pour les inputs GraphQL.
 *
 * Le calcul de `skip` vit ici et nulle part ailleurs : dupliqué, il finissait
 * par utiliser une taille de page en dur là où `take` utilisait la variable, et
 * la pagination aurait sauté le jour où la taille devient réglable.
 */
export function useTableQuery(initialPageSize: number = DEFAULT_PAGE_SIZE) {
  const [page, setPage] = useState<number>(1)
  const [pageSize, setPageSizeState] = useState<number>(initialPageSize)
  const [sortedInfo, setSortedInfo] = useState<SortedInfo | undefined>()

  const handleSort = (field: string) => {
    setSortedInfo({
      field,
      order: sortedInfo?.order === 'ascend' ? 'descend' : 'ascend',
    })
  }

  /**
   * Retour à la première page : à appeler dès qu'un filtre change, sinon on
   * reste sur une page 4 qui n'existe plus dans le résultat filtré. Référence
   * stable, pour être utilisable en dépendance d'effet (recherche débouncée).
   */
  const resetPage = useCallback(() => setPage(1), [])

  /** Change la taille de page en gardant la ligne courante à l'écran. */
  const setPageSize = useCallback((next: number) => {
    // Les deux `set` sont indépendants et l'updater reste pur : poser le
    // `setPage` à l'intérieur de celui de `setPageSizeState` aurait été plus
    // compact, mais React rejoue les updaters (StrictMode, rendu concurrent) et
    // le décalage de page se serait appliqué deux fois.
    setPage(previous => pageForNewSize(previous, pageSize, next))
    setPageSizeState(next)
  }, [pageSize])

  return {
    page,
    pageSize,
    setPage,
    setPageSize,
    resetPage,
    sortedInfo,
    handleSort,
    queryArgs: {
      skip: (page - 1) * pageSize,
      take: pageSize,
      orderBy: sortedInfo && returnOrderBy(sortedInfo.field, sortedInfo.order),
    },
  }
}
